import { Controller } from "@hotwired/stimulus";
import { formSubmitWithWaitForJobCompletion } from "../jobCompletion";
import * as Turbo from "@hotwired/turbo";
import { trackEvent } from "../../Features/Analytics";
import { getScoreFunctionPreservingOrderInSearch, selectize } from "../selectizeHelper";
import AddNewDestinationMapController from "../../Features/Destinations/Components/AddNewDestinationMap/AddNewDestinationMapController";
import { SearchDestinationDto } from "../_generated/SearchDestinationDto";
import { EventHandlerRegistration, registerGlobalEventHandlerEventName } from "../BodyController";
import { destinationSelectedEventName, noDestinationsFoundEventName } from "../../Features/Shared/Components/DestinationSearch/DestinationSearchController";
import { StimulusControllers } from "../_generated/StimulusControllers";

export default class AddNewDestinationController extends Controller {

    static targets = [
        "destinationUniqueId",
        "form",
        "submit",
        "language",
        "authorUserUniqueId",
        "contactSupportSection"
    ];

    static values = {
        getJobUrl: String,
        queryClonedDestinationUrl: String,
        destinationDetailUrl: String
    };

    readonly destinationUniqueIdTarget!: HTMLInputElement;
    readonly formTarget!: HTMLElement;
    readonly submitTarget!: HTMLElement;
    readonly languageTarget!: HTMLElement;
    readonly authorUserUniqueIdTarget!: HTMLElement;
    readonly contactSupportSectionTarget!: HTMLElement;

    readonly getJobUrlValue!: string;
    readonly queryClonedDestinationUrlValue!: string;
    readonly destinationDetailUrlValue!: string;

    connect() {
        this.registerDestinationSearchEventHandlers();
        let destinationDetailUrl = this.destinationDetailUrlValue;

        formSubmitWithWaitForJobCompletion(
            $(this.formTarget),
            $(this.submitTarget),
            this.getJobUrlValue,
            () => {
                const destinationUniqueId = $(this.destinationUniqueIdTarget).val() as string;
                const language = $(this.languageTarget).val() as string;
                const authorUserUniqueId = $(this.authorUserUniqueIdTarget).val() as string;

                const queryClonedDestinationUrl = this.queryClonedDestinationUrlValue
                    .replace("$baseDestinationUniqueId", destinationUniqueId)
                    .replace("$authorUserUniqueId", authorUserUniqueId);

                $.get({
                    url: queryClonedDestinationUrl,
                    success(clonedDestinationUniqueId) {

                        destinationDetailUrl = destinationDetailUrl
                            .replace("$uniqueId", clonedDestinationUniqueId)
                            .replace("$language", language);

                        trackEvent('destinationCreated', { language, baseDestinationUid: destinationUniqueId, destinationUid: clonedDestinationUniqueId });

                        Turbo.visit(destinationDetailUrl);
                    }
                });
            }
        );

        selectize($(this.languageTarget), {
            score: getScoreFunctionPreservingOrderInSearch
        });
    }

    registerDestinationSearchEventHandlers() {
        this.dispatch(registerGlobalEventHandlerEventName, {
            prefix: "",
            detail: new EventHandlerRegistration<SearchDestinationDto>(
                `${StimulusControllers.destinationSearch}:${destinationSelectedEventName}`,
                this.handleDestinationSelectedEvent.bind(this)
            )
        });
        this.dispatch(registerGlobalEventHandlerEventName, {
            prefix: "",
            detail: new EventHandlerRegistration<SearchDestinationDto>(
                `${StimulusControllers.destinationSearch}:${noDestinationsFoundEventName}`,
                this.handleNoDestinationsFoundEvent.bind(this)
            )
        });
    }

    handleDestinationSelectedEvent(event: CustomEventInit<SearchDestinationDto>) {
        this.refreshContactSupportSectionVisibility();

        const searchDestinationDto = event.detail;

        if (!searchDestinationDto || $.isEmptyObject(searchDestinationDto)) throw new Error("No SearchDestinationDto in the event detail.")

        this.getAddNewDestinationMapController().setDestinationMarkerAndBoundingBox(
            searchDestinationDto.boundingBoxCenter,
            searchDestinationDto.boundingBox
        );
    }

    handleNoDestinationsFoundEvent() {
        this.refreshContactSupportSectionVisibility();
    }

    refreshContactSupportSectionVisibility() {
        const selectedDestinationUniqueId = this.destinationUniqueIdTarget.value;
        this.contactSupportSectionTarget.style.display = selectedDestinationUniqueId ? 'none' : 'block';
    }

    cancel() {
        history.back();
    }

    private getAddNewDestinationMapController(): AddNewDestinationMapController {
        const addNewDestinationMapControllerName = "destinations--add-new-destination-map" 
        const addNewDestinationMap = $(`[data-controller='${addNewDestinationMapControllerName}']`);
        if (addNewDestinationMap.length === 0) throw new Error(`Cannot find ${addNewDestinationMapControllerName} controller element.`)
        return this.application.getControllerForElementAndIdentifier(addNewDestinationMap[0], addNewDestinationMapControllerName) as AddNewDestinationMapController;
    }
}
